import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Menu, X } from 'lucide-react';

const HeaderContainer = styled.header`
  background-color: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.85rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Logo = styled(Link)`
  font-family: 'Montserrat', 'Arial', sans-serif;
  font-size: 1.25rem;
  font-weight: 800;
  color: #2c3e50;
  text-decoration: none;
  white-space: nowrap;
  transition: color 0.3s ease;

  span {
    color: #3498db;
  }

  &:hover {
    color: #3498db;
  }
`;

const NavToggle = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: #3498db;

  @media (max-width: 768px) {
    display: block;
  }
`;

const Nav = styled.nav`
  @media (max-width: 768px) {
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    width: 100%;
    position: absolute;
    top: 100%;
    left: 0;
    background-color: #ffffff;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style-type: none;
  margin: 0;
  padding: 0;
  text-align: center;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const NavItem = styled.li`
  margin-left: 0.25rem;

  @media (max-width: 768px) {
    margin: 0;
  }
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 0.85rem;
  border-radius: 6px;
  color: #2c3e50;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: #eaf4fb;
    color: #2980b9;
  }

  @media (max-width: 768px) {
    padding: 1rem 2rem;
    border-bottom: 1px solid #ecf0f1;
  }
`;

const LookupButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.75rem;
  padding: 0.55rem 1.25rem;
  border-radius: 30px;
  background-color: #e74c3c;
  color: #ffffff;
  text-decoration: none;
  font-weight: 700;
  font-size: 0.95rem;
  white-space: nowrap;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(231, 76, 60, 0.35);

  &:hover {
    background-color: #c0392b;
    color: #ffffff;
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    margin: 0.75rem 2rem 1rem;
  }
`;

const DesktopNav = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileNav = styled.div`
  @media (min-width: 768px) {
    display: none;
  }
`;

const Header = () => {
  const [isNavOpen, setIsNavOpen] = useState(false);

  const toggleNav = () => {
    setIsNavOpen(!isNavOpen);
  };

  const navItems = [
    { to: "/", label: "Home" },
    { to: "/what-is-duns", label: "What is DUNS?" },
    { to: "/countries", label: "Countries" },
    { to: "/benefits", label: "Benefits" },
    { to: "/all-articles", label: "Articles" },
    { to: "/how-to-get", label: "Get a DUNS" },
  ];

  return (
    <HeaderContainer>
      <HeaderContent>
        <Logo to="/">Global <span>DUNS</span> Finder</Logo>
        <DesktopNav>
          <NavList>
            {navItems.map((item, index) => (
              <NavItem key={index}>
                <NavLink to={item.to}>{item.label}</NavLink>
              </NavItem>
            ))}
          </NavList>
          <LookupButton to="/duns-lookup">Free Lookup</LookupButton>
        </DesktopNav>
        <NavToggle onClick={toggleNav}>
          {isNavOpen ? <X size={24} /> : <Menu size={24} />}
        </NavToggle>
      </HeaderContent>
      <MobileNav>
        <Nav isOpen={isNavOpen}>
          <NavList>
            {navItems.map((item, index) => (
              <NavItem key={index}>
                <NavLink to={item.to} onClick={() => setIsNavOpen(false)}>
                  {item.label}
                </NavLink>
              </NavItem>
            ))}
          </NavList>
          <LookupButton to="/duns-lookup" onClick={() => setIsNavOpen(false)}>
            Free DUNS Lookup
          </LookupButton>
        </Nav>
      </MobileNav>
    </HeaderContainer>
  );
};

export default Header;